import Select from 'react-select';
import { useField } from 'formik';
import { getCustomStyles } from '@/theme/components/select';
import { NoOptionsMessage } from '@/components/select';
import { useEffect, useState } from 'react';
import { Text } from '@chakra-ui/react';
import { useSelectOptions } from './useSelectOptions';
import { customFilterStyles } from '@/design-system/select/customFilterStyles';
import {
  TooltipMultiValueLabel,
  TooltipSingleValue,
} from '@/design-system/select/tooltipValueComponents';
import { isEqual } from 'lodash-es';
import { KSelectStylesConfig } from '@/ui-library/design-system/constants/KSelect.styles';
import { useMenuRepositionOnResize } from '@/hooks/useMenuRepositionOnResize';

interface SelectFieldProps {
  item?: any;
  formik?: any;
}

export const SelectField = ({ item = {}, formik = {} }: SelectFieldProps) => {
  // Keeps the body-portaled menu anchored when chips change the control height.
  const selectWrapperRef = useMenuRepositionOnResize();
  const {
    name,
    options: staticOptions = [],
    isMulti,
    customFormatLabel,
    placeholder,
    label,
    disabled = false,
    components = {},
    menuPlacement = 'auto',
    // Defaults to react-select's 'absolute' positioning. Opt into 'fixed' for
    // body-portaled menus that live inside a scrollable popover/drawer: 'fixed'
    // makes react-select track ancestor scroll + control resize and recompute
    // the menu position, so it stays anchored instead of drifting to a screen
    // edge or overlapping the search input.
    menuPosition = 'absolute',
    isLoading: loadingFromProps = false,
    valueKey = 'value',
    labelKey = 'label',
    descKey = 'desc',
    fetchConfig = {},
    refreshOptions,
    updateEndpointOnChange = false,
    debounceTime = 500,
    disabledMessage = undefined,
    customNoOptionsMessage,
    newStyles = [],
    isClearable = false,
    defaultValue = null,
    customOnChange = () => {},
    OptionRightComp = () => <></>,
    containerRef = null,
    isSearchable = true,
    customOnInputChange,
    isNewFilter,
    isAppendToBody = false,
    getCustomOptions = () => [],
    menuIsOpen,
    filterOption,
    styleVersion = 'v2',
  } = item;

  const [_field, meta, helpers] = useField(name);
  const { setValue, setTouched, setError } = helpers;
  const { error, value: formikValue, touched } = meta;
  const [inputText, setInputText] = useState('');
  const { optionsData, isLoading, debouncedHandleInputChange } =
    useSelectOptions(fetchConfig, debounceTime);
  const styles = KSelectStylesConfig({
    variant: 'default',
    customStyles: newStyles,
  });
  const selectStyles =
    styleVersion === 'v1' ? getCustomStyles(newStyles) : styles;

  const { submitCount } = formik || {};
  const showError = (submitCount > 0 || touched) && error;

  const allOptions = [
    ...(Array.isArray(staticOptions) ? staticOptions : []),
    ...(Array.isArray(optionsData) ? optionsData : []),
    ...(getCustomOptions?.() || []),
  ];

  useEffect(() => {
    if (refreshOptions) {
      setValue(isMulti ? [] : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOptions]);

  const onChange = (selectedOption) => {
    setValue(
      isMulti
        ? selectedOption?.map((item) => item?.[valueKey])
        : selectedOption?.[valueKey]
    );
    customOnChange(selectedOption, formik);
    setTouched(true);
    setError(null);
  };

  const formatOptionLabel = (option, { context }) => {
    if (customFormatLabel)
      return customFormatLabel(option, labelKey, item, context);
    return option?.[labelKey];
  };

  const handleInputChange = (inputText, meta) => {
    setInputText(inputText);
    customOnInputChange?.(inputText, meta);

    if (updateEndpointOnChange && inputText?.length > 1) {
      debouncedHandleInputChange(inputText, meta);
    }
  };

  const NoOptionsMessageComp = (props) => {
    if (customNoOptionsMessage) return customNoOptionsMessage(props);
    return NoOptionsMessage(props);
  };

  const defaultOption = allOptions?.find(
    (item) => item?.[valueKey] === defaultValue
  );

  const getFlattenedOptions = (options) => {
    if (!Array.isArray(options)) return [];
    return options.reduce((acc, group) => {
      if (group.options) {
        return [...acc, ...group.options];
      }
      return [...acc, group];
    }, []);
  };

  // This is needed, in case we are using group options
  const flattenedOptions = getFlattenedOptions(allOptions);

  const value = !formikValue
    ? defaultOption || null
    : isMulti
      ? Array.isArray(formikValue)
        ? // Selection order, not option order — callers rely on it (e.g. job offices).
          formikValue
            .map((selected) =>
              flattenedOptions?.find((item) => item?.[valueKey] === selected)
            )
            .filter(Boolean)
        : defaultOption || null
      : // Change to isEqual to handle object comparison
        flattenedOptions?.find((item) =>
          isEqual(item?.[valueKey], meta?.value)
        );

  if (isAppendToBody) {
    return (
      <div ref={selectWrapperRef}>
        <Select
          isMulti={isMulti}
          isLoading={isLoading || loadingFromProps}
          inputValue={inputText}
          options={allOptions}
          inputId={name}
          isClearable={isClearable}
          isSearchable={isSearchable}
          className={`kula_custom_select__container`}
          classNamePrefix="kula_custom_select"
          styles={
            !isNewFilter
              ? KSelectStylesConfig({
                  variant: 'default',
                  customStyles: newStyles,
                })
              : customFilterStyles(newStyles)
          }
          // menuIsOpen
          menuPortalTarget={document.body}
          menuPosition={menuPosition}
          // menuShouldBlockScroll={true}
          menuShouldScrollIntoView={true}
          formatOptionLabel={formatOptionLabel}
          // onFocus={() => setTouched(true)}
          // onBlur={() => setTouched(true)}
          hasError={showError}
          menuPlacement={menuPlacement}
          isDisabled={disabled}
          onInputChange={handleInputChange}
          filterOption={filterOption}
          components={{
            IndicatorSeparator: null,
            NoOptionsMessage: NoOptionsMessageComp,
            MultiValueLabel: TooltipMultiValueLabel,
            SingleValue: TooltipSingleValue,
            ...components,
          }}
          placeholder={placeholder || `Select ${label?.toLowerCase()}`}
          value={value}
          getOptionLabel={(option) => option?.[labelKey]}
          getOptionValue={(option) => option?.[valueKey]}
          onChange={onChange}
          setInputText={setInputText}
          isOptionDisabled={(option) => option?.isDisabled}
          //? Extra props which are needed in react-select components can be passed here
          extraProps={{
            valueKey,
            labelKey,
            descKey,
            OptionRightComp,
            containerRef,
            ...item,
          }}
        />
      </div>
    );
  }

  return (
    <>
      <Select
        isMulti={isMulti}
        isLoading={isLoading || loadingFromProps}
        inputValue={inputText}
        options={allOptions}
        inputId={name}
        isClearable={isClearable}
        isSearchable={isSearchable}
        className={`kula_custom_select__container`}
        classNamePrefix="kula_custom_select"
        styles={!isNewFilter ? selectStyles : customFilterStyles(newStyles)}
        menuIsOpen={menuIsOpen}
        menuPosition={menuPosition}
        // menuShouldBlockScroll={true}
        menuShouldScrollIntoView={true}
        formatOptionLabel={formatOptionLabel}
        // onBlur={() => setTouched(true)}
        hasError={showError}
        menuPlacement={menuPlacement}
        isDisabled={disabled}
        onInputChange={handleInputChange}
        filterOption={filterOption}
        components={{
          IndicatorSeparator: null,
          NoOptionsMessage: NoOptionsMessageComp,
          MultiValueLabel: TooltipMultiValueLabel,
          SingleValue: TooltipSingleValue,
          ...components,
        }}
        placeholder={placeholder || `Select ${label?.toLowerCase()}`}
        value={value}
        getOptionLabel={(option) => option?.[labelKey]}
        getOptionValue={(option) => option?.[valueKey]}
        onChange={onChange}
        setInputText={setInputText}
        isOptionDisabled={(option) => option?.isDisabled}
        //? Extra props which are needed in react-select components can be passed here
        extraProps={{
          valueKey,
          labelKey,
          descKey,
          OptionRightComp,
          containerRef,
          ...item,
        }}
      />

      {disabled && disabledMessage && (
        <Text mt="5px" fontSize="sm" color="ats-negative">
          {disabledMessage}
        </Text>
      )}
    </>
  );
};
